/**
 * Phase 7-Lite — Privacy Policy & Terms of Service stubs (Feb 2026).
 *
 * Pilot-stage placeholder content. Clearly marked as "PILOT — pre-launch".
 * Replace with lawyer-reviewed copy before public launch.
 *
 * Two routes: /privacy and /terms — both publicly accessible (no login required)
 * so prospects + linked-from-email can read them.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const CodeFrame = ({ title, children }) => (
  <div className="min-h-screen bg-[#050003] text-[#A3C7A8] font-mono">
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link
        to="/login"
        className="inline-flex items-center gap-2 text-[#39FF14] hover:underline text-xs uppercase tracking-wider mb-8"
        data-testid="legal-back-link"
      >
        <ArrowLeft className="w-3 h-3" /> // BACK TO APP
      </Link>

      <div className="bg-[#FF9F0A] text-[#050003] px-3 py-2 mb-6 text-xs uppercase tracking-wider font-bold">
        // PILOT — PRE-LAUNCH PLACEHOLDER. NOT LEGALLY REVIEWED. CONTACT US BEFORE RELYING ON THIS DOCUMENT.
      </div>

      <h1 className="text-[#39FF14] text-3xl uppercase tracking-wider mb-4">{title}</h1>
      <p className="text-[#6B8E73] text-xs uppercase tracking-wider mb-8">// Last updated: February 2026</p>

      <div className="space-y-6 leading-relaxed text-sm">{children}</div>

      <div className="mt-12 pt-6 border-t border-[#2A0F3D] text-xs text-[#6B8E73]">
        Questions? Email{' '}
        <a href="mailto:ops@ashandmerch.com" className="text-[#39FF14] hover:underline">
          ops@ashandmerch.com
        </a>
      </div>
    </div>
  </div>
);

const ModernFrame = ({ title, children }) => (
  <div className="am-modern-scope am-legal-modern min-h-screen" style={{ fontFamily: 'var(--am-font-sans)' }}>
    <div className="max-w-3xl mx-auto px-5 py-8 sm:py-12">
      <Link to="/login" className="am-back-btn mb-8" data-testid="legal-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to app
      </Link>
      <div className="rounded-[10px] px-4 py-3 mb-6 text-sm" style={{ background: 'var(--am-gold-50)', border: '1px solid var(--am-gold-300)', color: 'var(--am-gold-700)' }}>
        Pilot, pre-launch placeholder. Not legally reviewed. Contact us before relying on this document.
      </div>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2" style={{ color: 'var(--am-ink-1)' }}>{title}</h1>
      <p className="text-sm mb-8" style={{ color: 'var(--am-ink-3)' }}>Last updated: February 2026</p>
      <div className="am-card p-5 sm:p-8 space-y-5 leading-relaxed text-[15px]">{children}</div>
      <div className="mt-8 text-sm" style={{ color: 'var(--am-ink-3)' }}>
        Questions? Email <a href="mailto:ops@ashandmerch.com" className="hover:underline">ops@ashandmerch.com</a>
      </div>
    </div>
  </div>
);

const Frame = (props) => {
  const { isModern } = useTheme();
  return isModern ? <ModernFrame {...props} /> : <CodeFrame {...props} />;
};

const H = ({ children }) => {
  const { isModern } = useTheme();
  if (isModern) {
    const text = typeof children === 'string' ? children.replace(/^\/\/\s*/, '') : children;
    const pretty = typeof text === 'string' ? text.charAt(0) + text.slice(1).toLowerCase() : text;
    return <h2 className="text-lg mt-6 mb-1 pb-1 border-b">{pretty}</h2>;
  }
  return <CodeH>{children}</CodeH>;
};

const CodeH = ({ children }) => (
  <h2 className="text-[#39FF14] text-lg uppercase tracking-wider mt-8 mb-2 border-b border-[#2A0F3D] pb-1">
    {children}
  </h2>
);

const P = ({ children }) => <p className="text-[#A3C7A8]">{children}</p>;

export function PrivacyPage() {
  return (
    <Frame title="Privacy Policy">
      <H>// SUMMARY</H>
      <P>
        Ash &amp; Merch ("we", "us") runs a hemp/CBD delivery service in the DMV (DC, Maryland,
        Virginia) and New Jersey regions. We collect the minimum information needed to deliver
        your orders. We never sell your data.
      </P>

      <H>// WHAT WE COLLECT</H>
      <P>
        <strong className="text-[#39FF14]">Account info:</strong> name, email, phone number, and
        delivery address. We use this to identify your account, deliver to you, and contact you
        about your order.
      </P>
      <P>
        <strong className="text-[#39FF14]">Order info:</strong> items you've ordered, total amount,
        payment method (cash or Cash App), and order status history. We retain this for accounting
        and so you can re-order favorites.
      </P>
      <P>
        <strong className="text-[#39FF14]">Location data:</strong> your delivery address (you
        provide this). For drivers, real-time GPS location during active deliveries (used to give
        customers ETAs). Driver location is NOT stored once delivery completes.
      </P>
      <P>
        <strong className="text-[#39FF14]">Device data:</strong> push notification tokens (only if
        you opt in). Web browser session cookies for keeping you logged in.
      </P>
      <P>
        <strong className="text-[#39FF14]">Loyalty / referral:</strong> your points balance, who
        referred you, and orders that earned/redeemed points. Used to credit and debit your
        loyalty account.
      </P>

      <H>// WHAT WE DON'T COLLECT</H>
      <P>
        We do NOT collect: government-issued ID images (planned for post-pilot once age
        verification rolls out), social security numbers, full payment card details (Cash App
        handles payments — we never see your card), or location data when you're not actively
        delivering.
      </P>

      <H>// THIRD PARTIES WE USE</H>
      <P>
        <strong className="text-[#39FF14]">Mapbox</strong> — geocoding addresses and turn-by-turn
        directions for drivers. Mapbox sees the addresses we send for routing.
      </P>
      <P>
        <strong className="text-[#39FF14]">Sentry</strong> — error monitoring. When something
        breaks, we send the error details to Sentry so we can fix it. Personal info (email, name)
        is automatically redacted before sending.
      </P>
      <P>
        <strong className="text-[#39FF14]">Cash App</strong> — when you pay via Cash App, you're
        sending money directly through Cash App. We never see your card or bank details.
      </P>
      <P>
        <strong className="text-[#39FF14]">MongoDB</strong> — our database. Hosted in the United States.
      </P>
      <P>We do NOT use Google Analytics, Facebook Pixel, or any advertising trackers.</P>

      <H>// HOW LONG WE KEEP YOUR DATA</H>
      <P>
        Active accounts: as long as your account is open. Closed accounts: 90 days for accounting,
        then deleted. Order history: 7 years (US tax compliance). Push notification tokens:
        deleted automatically when expired or unsubscribed.
      </P>

      <H>// YOUR RIGHTS</H>
      <P>
        You can: ask us to show you what data we have about you, ask us to delete your account
        (we'll delete personal info and anonymize order history for accounting), unsubscribe from
        push notifications anytime in your browser settings or our app settings.
      </P>
      <P>
        Email <a href="mailto:ops@ashandmerch.com" className="text-[#39FF14] hover:underline">ops@ashandmerch.com</a> for
        any privacy request — we'll respond within 7 days.
      </P>

      <H>// SECURITY</H>
      <P>
        Passwords are hashed (bcrypt). Sessions use HTTP-only cookies. The site uses HTTPS in
        production. Daily encrypted backups. Rate limiting protects against brute-force attacks.
      </P>

      <H>// AGE & LEGAL USE</H>
      <P>
        You must be 21+ and located in our service regions (DMV or NJ) to use Ash &amp; Merch.
        Hemp/CBD products comply with federal and applicable state law. We may verify your age
        before delivery.
      </P>

      <H>// CHANGES</H>
      <P>
        We may update this policy. We'll notify you in the app and via email at least 14 days
        before material changes take effect.
      </P>

      <H>// CONTACT</H>
      <P>Questions, requests, complaints: <a href="mailto:ops@ashandmerch.com" className="text-[#39FF14] hover:underline">ops@ashandmerch.com</a></P>
    </Frame>
  );
}

export function TermsPage() {
  return (
    <Frame title="Terms of Service">
      <H>// AGREEMENT</H>
      <P>
        By using Ash &amp; Merch ("the App"), you agree to these Terms. If you don't agree, please
        don't use the App.
      </P>

      <H>// WHO CAN USE</H>
      <P>
        You must be at least 21 years old, physically located in our service regions (DC, Maryland,
        Virginia, or New Jersey at the time of order), and legally allowed to purchase hemp/CBD
        products in your state. Pilot phase is invite-only — you need a referral code from an
        existing user or staff member.
      </P>

      <H>// THE SERVICE</H>
      <P>
        We deliver legal hemp/CBD products to your door. We make no medical claims. Products are
        for use by adults 21+. Driver may verify age before handoff.
      </P>

      <H>// ORDERS &amp; PAYMENT</H>
      <P>
        You can pay cash on delivery or via Cash App (if enabled). Orders are confirmed by the
        dispatcher and delivered by our driver. Pricing shown at checkout is the price you pay
        (no hidden fees). Loyalty points and referral discounts are applied per the program rules.
      </P>
      <P>
        Cancellations: you can cancel before the order moves to "preparing" via the app. After
        that, contact us — we'll honor in-good-faith requests when reasonable.
      </P>

      <H>// REFUNDS</H>
      <P>
        If we deliver the wrong item or product is defective, we'll refund or replace it. Contact
        us within 24 hours of delivery. Refunds for Cash App pre-payments are processed back via
        Cash App. Cash refunds are settled in cash by the driver or in-person handoff.
      </P>

      <H>// CONDUCT</H>
      <P>
        Don't: resell our products, abuse our drivers, fraudulently claim non-delivery, share your
        invite code on public forums (it's pilot-only), or use the App for anything illegal.
      </P>
      <P>
        We may suspend or close accounts that violate these terms. We may also terminate accounts
        that have no activity for 12+ months.
      </P>

      <H>// LOYALTY &amp; REFERRALS</H>
      <P>
        Points are earned at our discretion (currently: 1 point per $1 spent on delivered orders;
        20 points = $1 redemption value). Referral codes credit both you and the new user when
        they place their first delivered order. Points have no cash value, can't be transferred
        or exchanged, and may be revoked if abuse is detected.
      </P>

      <H>// LIABILITY</H>
      <P>
        We do our best to deliver on time and accurately. We're not liable for delays caused by
        weather, traffic, or events outside our control. The App is provided "as is" without
        warranties of any kind. To the extent permitted by law, our liability for any claim is
        limited to the amount you paid us in the 30 days before the claim.
      </P>

      <H>// CHANGES TO THE SERVICE</H>
      <P>
        Pilot phase means features may change quickly. We may add, remove, or modify features at
        any time. We'll communicate material changes via the App or email.
      </P>

      <H>// GOVERNING LAW</H>
      <P>
        These Terms are governed by the laws of the United States and the state where Ash &amp;
        Merch is registered. Disputes will be resolved in courts of competent jurisdiction in
        that state.
      </P>

      <H>// CONTACT</H>
      <P>Questions: <a href="mailto:ops@ashandmerch.com" className="text-[#39FF14] hover:underline">ops@ashandmerch.com</a></P>
    </Frame>
  );
}
